// Active-ragdoll motor: joint-space "muscles" plus a pelvis upright-balance
// assist (applyUprightBalance). No `three` import.
//
// v0.5 addendum (box3d-web SOLVER-INTEGRATED JOINT MOTORS, see
// ../../../three-box3d/docs/api-design.md §7): every joint now DRIVES toward
// its pose target through `world.setRevoluteMotor`/`setSphericalMotor`
// (velocity-servo: declare a target angular speed/velocity + a torque budget
// once, the constraint solver enforces the torque clamp every substep while
// continuously correcting toward it) instead of an externally-applied torque
// IMPULSE recomputed and reapplied every frame by this file. This is a
// meaningfully different control law, not just a faster way to do the same
// thing: a motor's target-velocity term is inherently damped (any deviation
// between actual and target velocity is corrected by the solver itself, at
// up to `maxTorque`), so a pure P-on-angle-error -> target-speed scheme gets
// PD-like behavior without an explicit derivative term or the impulse
// controller's oscillation risk — measured here to run far stiffer (higher
// effective gain) before showing any ringing. See applyPoseHoldMotors below.
//
// CAPABILITY FALLBACK: every function that used to be impulse-only now takes
// an optional trailing `caps?: HumanoidMotorCapabilities`. Omitting it (or
// passing `{ jointMotors: false }`, e.g. a stale wasm build) keeps the
// ORIGINAL impulse-PD control law byte-for-byte -- `applyPoseHoldImpulse` and
// `applyGetUpImpulseLegacy` are the pre-v0.5 bodies, untouched, still fully
// exported. Every caller in this codebase now probes `Capabilities.jointMotors`
// once at runtime creation and threads it through, but nothing NEW is
// required to keep working: all five pre-existing exported signatures
// (`applyPoseHold`, `applyUprightBalance`, `applyStandAssist`, `applyGetUp`,
// plus the ramp/state helpers) are unchanged in arity except for the new
// trailing optional parameter.
//
// HumanoidPose is a TARGET RELATIVE ROTATION per joint: the child body's
// orientation relative to its parent's CURRENT world orientation, expressed as
// the same convention the T-pose spawn itself uses (identity for every joint
// except the shoulders, whose canonical arm bodies are pre-rotated 90 degrees
// to lie along +/-X — see humanoid.ts's `computeCanonicalLayout`).
import type { BodyHandle, TransformBuffer, Vec3Out, World } from 'box3d-web';
import {
  HUMANOID_BONE_NAMES,
  HUMANOID_JOINT_DEFS,
  HUMANOID_MASS_FRACTIONS,
  type HumanoidJointDef,
  type HumanoidJointName,
} from './humanoidBones';
import {
  conjugateQuat,
  multiplyQuat,
  quatAroundZ,
  rotateVectorByQuat,
  type Humanoid,
} from './humanoid';
import type { MuscleJointCommand } from './humanoidMuscleController';
import type { QuatTuple, Vec3Tuple } from './humanoidSpec';

/** Prefer the rig's actual layout-resolved defs (spherical ankles etc.). */
function jointDefsOf(humanoid: Humanoid): readonly HumanoidJointDef[] {
  return humanoid.jointDefs ?? HUMANOID_JOINT_DEFS;
}

function quatAroundX(angleRad: number): QuatTuple {
  return [Math.sin(angleRad / 2), 0, 0, Math.cos(angleRad / 2)];
}

/**
 * Spherical muscle commands store independent sagittal (X) and lateral (Z)
 * hinge-like offsets in `targetRotationVectorRad = [sag, 0, lat]`.
 *
 * These are NOT a single axis-angle of the vector (sag,0,lat) — that mixed
 * both DOFs onto a diagonal axis and destroyed horizontal foot placement
 * (cp14 calibration). Compose as Rx(sag) then Rz(lat) in parent-local frame:
 *   q = Rz(lat) * Rx(sag)
 * so pure sag still matches quatAroundX / TUCK flex, and pure lat matches
 * abduction about Z (FK: lat+ maps hanging −Y thigh toward +X).
 */
export function muscleSphericalTargetQuat(
  sagRad: number,
  latRad: number,
): QuatTuple {
  const qx = quatAroundX(sagRad);
  const qz = quatAroundZ(latRad);
  return multiplyQuat(qz, qx);
}

export type HumanoidPose = Partial<Record<HumanoidJointName, QuatTuple>>;

/** Probe result this file needs to pick motor vs. legacy-impulse control.
 *  Callers get this from `box3d-web`'s `probeCapabilities(world).jointMotors`,
 *  probed ONCE at runtime/world creation (see humanoidLabPhysics.ts and
 *  humanoidEvaluation.ts) -- never re-probed per frame. */
export interface HumanoidMotorCapabilities {
  jointMotors: boolean;
  /** Solver motor-torque readback (`getRevoluteMotorTorque` /
   *  `getSphericalMotorTorque`). Required for Gate A saturation cert. */
  jointMotorTelemetry?: boolean;
}

/**
 * Saturation threshold: actual solver torque (abs / spherical magnitude) is
 * considered saturated when ≥ this fraction of commanded maxTorque (>0).
 */
export const MOTOR_SATURATION_FRACTION_THRESHOLD = 0.98 as const;

/** Per-joint commanded motor inputs preserved for one fixed step. */
export interface HumanoidJointMotorCommandRecord {
  /** Revolute target speed (rad/s), or spherical velocity magnitude (rad/s). */
  commandedSpeed: number;
  /** Commanded torque budget (N·m). */
  torqueCap: number;
  /**
   * True when this joint uses a revolute hinge motor (scalar torque readback).
   * False for spherical motors (vector magnitude readback).
   */
  revolute: boolean;
  /**
   * Diagnostic only (optional): muscle command hinge/sagittal target (rad).
   * Captured at drive time; does not affect motor behavior. Omitted by legacy
   * fixtures that build command records by hand.
   */
  targetOffsetRad?: number;
  /**
   * Diagnostic only (optional): muscle command rotation-vector target (rad).
   * Revolute: typically [offset, 0, 0]; spherical: [sag, 0, lat].
   */
  targetRotationVectorRad?: readonly [number, number, number];
  /**
   * Diagnostic only (optional): revolute pose-error twist about hinge axis (rad)
   * from computeJointPoseError at the command frame. Sign matches motor speed law.
   */
  poseErrorTwistRad?: number;
  /**
   * Diagnostic only (optional): spherical pose-error axis-angle vector (rad)
   * from computeJointPoseError at the command frame.
   */
  poseErrorAxisAngleRad?: readonly [number, number, number];
  /**
   * Diagnostic only (optional): signed spherical motor commanded velocity
   * vector (rad/s) in solver/motor coordinates after clamp — the same tuple
   * passed to setSphericalMotor. Omitted for revolute joints and legacy
   * hand-built fixtures. Not a world-frame, CoP, or ground-reaction estimate.
   */
  commandedVelocityVectorRadS?: readonly [number, number, number];
}

/** Per-joint post-step solver telemetry. */
export interface HumanoidJointMotorTelemetrySample {
  commandedSpeed: number;
  torqueCap: number;
  /** Actual solver torque: |revolute| or spherical vector magnitude (N·m). */
  actualTorque: number;
  saturated: boolean;
  revolute: boolean;
  /** Diagnostic only — copied from the command record when present. */
  targetOffsetRad?: number;
  /** Diagnostic only — copied from the command record when present. */
  targetRotationVectorRad?: readonly [number, number, number];
  /** Diagnostic only — revolute pose error twist (rad). */
  poseErrorTwistRad?: number;
  /** Diagnostic only — spherical pose error axis-angle (rad). */
  poseErrorAxisAngleRad?: readonly [number, number, number];
  /**
   * Diagnostic only (optional): signed spherical motor commanded velocity
   * vector (rad/s) in solver/motor coordinates — copied from the command
   * record when present. Omitted for revolute / legacy fixtures.
   */
  commandedVelocityVectorRadS?: readonly [number, number, number];
  /**
   * Diagnostic only (optional): signed spherical motor torque vector (N·m)
   * from getSphericalMotorTorque in solver/motor coordinates, before the
   * magnitude is taken for actualTorque. Omitted for revolute joints.
   * Not a world-frame, CoP, ZMP, or ground-reaction estimate.
   */
  actualTorqueVectorNm?: readonly [number, number, number];
}

/** Aggregate + per-joint motor telemetry snapshot for the last fixed step. */
export interface HumanoidMotorTelemetrySnapshot {
  activeMotorCount: number;
  saturatedMotorCount: number;
  /** saturatedMotorCount / activeMotorCount (0 when no active motors). */
  saturationFraction: number;
  joints: Readonly<Partial<Record<HumanoidJointName, HumanoidJointMotorTelemetrySample>>>;
}

export type HumanoidMotorCommandBuffer = Map<HumanoidJointName, HumanoidJointMotorCommandRecord>;

export function createMotorCommandBuffer(): HumanoidMotorCommandBuffer {
  return new Map();
}

export function emptyMotorTelemetrySnapshot(): HumanoidMotorTelemetrySnapshot {
  return {
    activeMotorCount: 0,
    saturatedMotorCount: 0,
    saturationFraction: 0,
    joints: {},
  };
}

/**
 * After `world.step`, read actual solver motor torques for every joint that
 * was commanded active this step (torqueCap > 0). Does not invent values when
 * `jointMotorTelemetry` is false — returns empty snapshot.
 */
export function readHumanoidMotorTelemetry(
  world: World,
  humanoid: Humanoid,
  commanded: ReadonlyMap<HumanoidJointName, HumanoidJointMotorCommandRecord>,
  jointMotorTelemetry: boolean,
): HumanoidMotorTelemetrySnapshot {
  if (!jointMotorTelemetry || commanded.size === 0) {
    return emptyMotorTelemetrySnapshot();
  }

  const joints: Partial<Record<HumanoidJointName, HumanoidJointMotorTelemetrySample>> = {};
  let activeMotorCount = 0;
  let saturatedMotorCount = 0;

  for (const [name, cmd] of commanded) {
    if (!(cmd.torqueCap > 0)) continue;
    const handle = humanoid.joints[name];
    let actualTorque: number;
    let actualTorqueVectorNm: readonly [number, number, number] | undefined;
    if (cmd.revolute) {
      actualTorque = Math.abs(world.getRevoluteMotorTorque(handle));
    } else {
      const v = world.getSphericalMotorTorque(handle);
      // Solver/motor-coordinate torque vector (diagnostic only) — capture
      // before collapsing to magnitude for existing actualTorque/saturation.
      actualTorqueVectorNm = [v.x, v.y, v.z];
      actualTorque = Math.hypot(v.x, v.y, v.z);
    }
    const saturated =
      cmd.torqueCap > 0 && actualTorque >= MOTOR_SATURATION_FRACTION_THRESHOLD * cmd.torqueCap;
    activeMotorCount += 1;
    if (saturated) saturatedMotorCount += 1;
    const sample: HumanoidJointMotorTelemetrySample = {
      commandedSpeed: cmd.commandedSpeed,
      torqueCap: cmd.torqueCap,
      actualTorque,
      saturated,
      revolute: cmd.revolute,
    };
    // Optional diagnostic pass-through (absent on hand-built fixtures).
    if (cmd.targetOffsetRad !== undefined) sample.targetOffsetRad = cmd.targetOffsetRad;
    if (cmd.targetRotationVectorRad !== undefined) {
      sample.targetRotationVectorRad = [
        cmd.targetRotationVectorRad[0],
        cmd.targetRotationVectorRad[1],
        cmd.targetRotationVectorRad[2],
      ];
    }
    if (cmd.poseErrorTwistRad !== undefined) sample.poseErrorTwistRad = cmd.poseErrorTwistRad;
    if (cmd.poseErrorAxisAngleRad !== undefined) {
      sample.poseErrorAxisAngleRad = [
        cmd.poseErrorAxisAngleRad[0],
        cmd.poseErrorAxisAngleRad[1],
        cmd.poseErrorAxisAngleRad[2],
      ];
    }
    if (cmd.commandedVelocityVectorRadS !== undefined) {
      sample.commandedVelocityVectorRadS = [
        cmd.commandedVelocityVectorRadS[0],
        cmd.commandedVelocityVectorRadS[1],
        cmd.commandedVelocityVectorRadS[2],
      ];
    }
    if (actualTorqueVectorNm !== undefined) {
      sample.actualTorqueVectorNm = [
        actualTorqueVectorNm[0],
        actualTorqueVectorNm[1],
        actualTorqueVectorNm[2],
      ];
    }
    joints[name] = sample;
  }

  return {
    activeMotorCount,
    saturatedMotorCount,
    saturationFraction: activeMotorCount > 0 ? saturatedMotorCount / activeMotorCount : 0,
    joints,
  };
}

/**
 * Per-joint aggregate motor diagnostics over a trial (Gate A instrumentation).
 * Denominator for saturationFraction is activeSamples (not wall-clock steps).
 */
export interface JointMotorTrialDiagnostics {
  activeSamples: number;
  saturatedSamples: number;
  /** saturatedSamples / activeSamples (0 when no active samples). */
  saturationFraction: number;
  longestContinuousSaturationS: number;
  maxCapNm: number;
  maxActualTorqueNm: number;
}

/**
 * Joints that count toward Gate A / P1A motor-authority pass/fail
 * (spec amendment 2026-07-29): hips, knees, ankles, spine.
 * Shoulders/elbows/neck remain in per-joint telemetry but are excluded from
 * continuous-run max and aggregate saturation fraction used by the gate.
 */
export const LOCOMOTION_GATE_SATURATION_JOINT_NAMES = [
  'leftHip',
  'rightHip',
  'leftKnee',
  'rightKnee',
  'leftAnkle',
  'rightAnkle',
  'spine',
] as const;

export type LocomotionGateSaturationJointName =
  (typeof LOCOMOTION_GATE_SATURATION_JOINT_NAMES)[number];

const LOCOMOTION_GATE_SATURATION_JOINT_SET: ReadonlySet<string> = new Set(
  LOCOMOTION_GATE_SATURATION_JOINT_NAMES,
);

/** True when `name` is in the locomotion-scoped saturation gate set. */
export function isLocomotionGateSaturationJoint(name: string): boolean {
  return LOCOMOTION_GATE_SATURATION_JOINT_SET.has(name);
}

/** Mutable accumulator for multi-step motor saturation aggregation. */
export interface MotorSaturationAccumulator {
  totalActiveSamples: number;
  totalSaturatedSamples: number;
  continuousSaturationMaxS: number;
  /** Continuous saturated run length (s) per joint; reset on inactive/absent. */
  jointRunS: Map<string, number>;
  joints: Map<
    string,
    {
      activeSamples: number;
      saturatedSamples: number;
      longestContinuousSaturationS: number;
      maxCapNm: number;
      maxActualTorqueNm: number;
    }
  >;
}

export function createMotorSaturationAccumulator(): MotorSaturationAccumulator {
  return {
    totalActiveSamples: 0,
    totalSaturatedSamples: 0,
    continuousSaturationMaxS: 0,
    jointRunS: new Map(),
    joints: new Map(),
  };
}

/**
 * Fold one fixed-step motor telemetry snapshot into a trial accumulator.
 *
 * Rules (tested):
 * - total denominator is sum of activeMotorCount across steps;
 * - a joint's continuous saturation run resets when it is absent/inactive
 *   (not present with torqueCap>0) in a step, not only when unsaturated;
 * - per-joint max cap / actual torque are maxima over active samples.
 */
export function accumulateMotorSaturationStep(
  acc: MotorSaturationAccumulator,
  snapshot: Pick<
    HumanoidMotorTelemetrySnapshot,
    'activeMotorCount' | 'saturatedMotorCount' | 'joints'
  >,
  dt: number,
): void {
  const safeDt = Math.max(0, dt);
  acc.totalActiveSamples += snapshot.activeMotorCount;
  acc.totalSaturatedSamples += snapshot.saturatedMotorCount;

  const activeThisStep = new Set<string>();
  for (const [jName, jSample] of Object.entries(snapshot.joints)) {
    if (!jSample) continue;
    activeThisStep.add(jName);
    let entry = acc.joints.get(jName);
    if (!entry) {
      entry = {
        activeSamples: 0,
        saturatedSamples: 0,
        longestContinuousSaturationS: 0,
        maxCapNm: 0,
        maxActualTorqueNm: 0,
      };
      acc.joints.set(jName, entry);
    }
    entry.activeSamples += 1;
    entry.maxCapNm = Math.max(entry.maxCapNm, jSample.torqueCap);
    entry.maxActualTorqueNm = Math.max(entry.maxActualTorqueNm, jSample.actualTorque);
    if (jSample.saturated) {
      entry.saturatedSamples += 1;
      const run = (acc.jointRunS.get(jName) ?? 0) + safeDt;
      acc.jointRunS.set(jName, run);
      entry.longestContinuousSaturationS = Math.max(entry.longestContinuousSaturationS, run);
      acc.continuousSaturationMaxS = Math.max(acc.continuousSaturationMaxS, run);
    } else {
      acc.jointRunS.set(jName, 0);
    }
  }

  // Reset continuous runs for joints that were tracked but inactive/absent this step.
  for (const jName of acc.jointRunS.keys()) {
    if (!activeThisStep.has(jName)) {
      acc.jointRunS.set(jName, 0);
    }
  }
}

/** Finalize aggregate + per-joint diagnostics from an accumulator. */
export function finalizeMotorSaturationDiagnostics(
  acc: MotorSaturationAccumulator,
  instrumented: boolean,
): {
  /**
   * Gate-scoped aggregate: saturated/active over locomotion joints only
   * (hips/knees/ankles/spine). Spec amendment 2026-07-29.
   */
  motorSaturationFraction: number | null;
  /**
   * Gate-scoped continuous max over locomotion joints only.
   * Full per-joint runs remain in jointMotorDiagnostics (all joints).
   */
  continuousSaturationMaxS: number | null;
  /** Per-joint diagnostics for ALL joints (including arms/neck). */
  jointMotorDiagnostics: Record<string, JointMotorTrialDiagnostics> | null;
} {
  if (!instrumented) {
    return {
      motorSaturationFraction: null,
      continuousSaturationMaxS: null,
      jointMotorDiagnostics: null,
    };
  }
  const jointMotorDiagnostics: Record<string, JointMotorTrialDiagnostics> = {};
  let gateActiveSamples = 0;
  let gateSaturatedSamples = 0;
  let gateContinuousMaxS = 0;
  for (const [name, entry] of acc.joints) {
    jointMotorDiagnostics[name] = {
      activeSamples: entry.activeSamples,
      saturatedSamples: entry.saturatedSamples,
      saturationFraction:
        entry.activeSamples > 0 ? entry.saturatedSamples / entry.activeSamples : 0,
      longestContinuousSaturationS: entry.longestContinuousSaturationS,
      maxCapNm: entry.maxCapNm,
      maxActualTorqueNm: entry.maxActualTorqueNm,
    };
    if (isLocomotionGateSaturationJoint(name)) {
      gateActiveSamples += entry.activeSamples;
      gateSaturatedSamples += entry.saturatedSamples;
      gateContinuousMaxS = Math.max(
        gateContinuousMaxS,
        entry.longestContinuousSaturationS,
      );
    }
  }
  return {
    motorSaturationFraction:
      gateActiveSamples > 0 ? gateSaturatedSamples / gateActiveSamples : 0,
    continuousSaturationMaxS: gateContinuousMaxS,
    jointMotorDiagnostics,
  };
}

const IDENTITY_QUAT: QuatTuple = [0, 0, 0, 1];
// Mirrors humanoid.ts's canonical T-pose arm rotation (quatAroundZ(+/-90deg)).
const LEFT_ARM_T_POSE_ROTATION = quatAroundZ(Math.PI / 2);
const RIGHT_ARM_T_POSE_ROTATION = quatAroundZ(-Math.PI / 2);

/** The rig's spawn orientation, per joint. Holding this pose at strength 1
 *  should produce ~zero corrective torque once settled. */
export const T_POSE: Readonly<HumanoidPose> = {
  spine: IDENTITY_QUAT,
  neck: IDENTITY_QUAT,
  leftShoulder: LEFT_ARM_T_POSE_ROTATION,
  rightShoulder: RIGHT_ARM_T_POSE_ROTATION,
  leftElbow: IDENTITY_QUAT,
  rightElbow: IDENTITY_QUAT,
  leftHip: IDENTITY_QUAT,
  rightHip: IDENTITY_QUAT,
  leftKnee: IDENTITY_QUAT,
  rightKnee: IDENTITY_QUAT,
  leftAnkle: IDENTITY_QUAT,
  rightAnkle: IDENTITY_QUAT,
};

/** Arms down at the sides (shoulders back to the capsule's natural
 *  Y-aligned orientation), everything else identical to T_POSE. */
export const STAND_POSE: Readonly<HumanoidPose> = {
  ...T_POSE,
  leftShoulder: IDENTITY_QUAT,
  rightShoulder: IDENTITY_QUAT,
};

// Get-up stage 1 (reorient) target: knees/hips bent into a tuck, elbows bent
// in -- NOT STAND_POSE. Measured (scripts/_diag-getup-trace.ts, throwaway):
// driving straight-legged STAND_POSE motors at near-full strength while the
// rig is lying on the ground builds a rigid, wide, straight "board" (torso +
// fully extended legs in one line) that a single hips-only righting torque
// cannot roll -- it hit a dead stable equilibrium at ~88deg tilt (still
// lying flat) and stopped accelerating entirely (measured hips angular speed
// pinned at 0.00-0.03rad/s indefinitely). A tucked pose shrinks the rig's
// footprint and rotational inertia and gets the knees away from the "long
// lever flat on the ground" geometry, giving the same righting torque
// something it can actually roll. Knee/hip flexion signs verified via that
// same diag script (printed pelvis height dropping into a crouch, not
// rising, when this pose is held on an already-standing rig).
const HIP_TUCK_ROTATION = quatAroundX((-100 * Math.PI) / 180);
const KNEE_TUCK_ROTATION = quatAroundX((-100 * Math.PI) / 180);
const ELBOW_TUCK_ROTATION_LEFT = quatAroundZ((70 * Math.PI) / 180);
const ELBOW_TUCK_ROTATION_RIGHT = quatAroundZ((-70 * Math.PI) / 180);

export const TUCK_POSE: Readonly<HumanoidPose> = {
  ...STAND_POSE,
  leftHip: HIP_TUCK_ROTATION,
  rightHip: HIP_TUCK_ROTATION,
  leftKnee: KNEE_TUCK_ROTATION,
  rightKnee: KNEE_TUCK_ROTATION,
  leftElbow: ELBOW_TUCK_ROTATION_LEFT,
  rightElbow: ELBOW_TUCK_ROTATION_RIGHT,
};

// --- shared math helpers (used by both the legacy impulse path and the new
// motor path) ---

function scalarInertia(world: World, body: BodyHandle): number {
  const inertia = world.getBodyInertia(body);
  return Math.max((inertia.x + inertia.y + inertia.z) / 3, 1e-8);
}

function dotVec3(a: Vec3Tuple, b: Vec3Tuple): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function normalizeQuat(q: QuatTuple): QuatTuple {
  const len = Math.hypot(q[0], q[1], q[2], q[3]) || 1;
  return [q[0] / len, q[1] / len, q[2] / len, q[3] / len];
}

function readBufferQuat(buffer: TransformBuffer, body: BodyHandle): QuatTuple {
  const offset = buffer.offsetOf(body);
  if (offset === undefined) throw new RangeError('humanoidMotor: body is not tracked by the given TransformBuffer.');
  const t = buffer.transforms;
  return normalizeQuat([t[offset + 3], t[offset + 4], t[offset + 5], t[offset + 6]]);
}

function readBufferPosition(buffer: TransformBuffer, body: BodyHandle): Vec3Tuple {
  const offset = buffer.offsetOf(body);
  if (offset === undefined) throw new RangeError('humanoidMotor: body is not tracked by the given TransformBuffer.');
  const t = buffer.transforms;
  return [t[offset], t[offset + 1], t[offset + 2]];
}

function clampVectorMagnitude(v: Vec3Tuple, maxLength: number): Vec3Tuple {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len <= maxLength || len < 1e-9) return v;
  const scale = maxLength / len;
  return [v[0] * scale, v[1] * scale, v[2] * scale];
}

function clampScalar(value: number, maxAbs: number): number {
  return Math.max(-maxAbs, Math.min(maxAbs, value));
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** Per-joint quaternion error decomposed into a world-space axis*angle vector
 *  (spherical joints) plus, for revolute joints, the twist scalar about the
 *  joint's own spawn-time hinge axis -- shared by the legacy torque-impulse
 *  path and the new motor path so both compute the identical error signal. */
interface JointPoseError {
  /** World-space axis*angle rotation vector taking the child from its
   *  current orientation to the pose target (full 3-DOF; for a revolute
   *  joint only the `hingeAxis`-aligned component is physically actuatable). */
  errorAxisAngle: Vec3Tuple;
  /** Revolute joints only: signed twist error (radians) about the joint's
   *  own world-space hinge axis at spawn. */
  twistRad: number;
  /** child angular velocity minus parent angular velocity, world space. */
  relativeAngularVelocity: Vec3Tuple;
  childMassKg: number;
  childInertia: number;
}

function computeJointPoseError(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  def: HumanoidJointDef,
  targetRelative: QuatTuple,
): JointPoseError {
  const parentBody = humanoid.bodies[def.parent];
  const childBody = humanoid.bodies[def.child];

  const parentRotation = readBufferQuat(buffer, parentBody);
  const childRotation = readBufferQuat(buffer, childBody);
  const targetChildRotation = normalizeQuat(multiplyQuat(parentRotation, targetRelative));
  let errorQuat = multiplyQuat(targetChildRotation, conjugateQuat(childRotation));
  if (errorQuat[3] < 0) errorQuat = [-errorQuat[0], -errorQuat[1], -errorQuat[2], -errorQuat[3]];
  const angle = 2 * Math.acos(Math.min(1, Math.max(-1, errorQuat[3])));
  const axisLength = Math.hypot(errorQuat[0], errorQuat[1], errorQuat[2]);
  const axis: Vec3Tuple = axisLength > 1e-6
    ? [errorQuat[0] / axisLength, errorQuat[1] / axisLength, errorQuat[2] / axisLength]
    : [0, 0, 0];
  const errorAxisAngle: Vec3Tuple = [axis[0] * angle, axis[1] * angle, axis[2] * angle];

  const parentAngularVelocity = world.getAngularVelocity(parentBody);
  const childAngularVelocity = world.getAngularVelocity(childBody);
  const relativeAngularVelocity: Vec3Tuple = [
    childAngularVelocity.x - parentAngularVelocity.x,
    childAngularVelocity.y - parentAngularVelocity.y,
    childAngularVelocity.z - parentAngularVelocity.z,
  ];

  let twistRad = 0;
  if (def.type === 'revolute') {
    const hingeAxis = humanoid.jointAxes[def.name] ?? [0, 0, 1];
    twistRad = 2 * Math.atan2(dotVec3([errorQuat[0], errorQuat[1], errorQuat[2]], hingeAxis), errorQuat[3]);
  }

  return {
    errorAxisAngle,
    twistRad,
    relativeAngularVelocity,
    childMassKg: world.getBodyMass(childBody),
    childInertia: scalarInertia(world, childBody),
  };
}

// =====================================================================
// Legacy impulse-PD path (pre-v0.5, UNCHANGED) -- used whenever
// `caps.jointMotors` is false/absent.
// =====================================================================

// Base torque budget, N*m per kg of the CHILD segment. First-cut values tuned
// against the poseHold evaluation gate (see humanoidEvaluation.ts) — larger
// segments (hip/knee) get more authority than distal ones (ankle/elbow).
const MAX_TORQUE_NM_PER_KG: Readonly<Record<HumanoidJointName, number>> = {
  spine: 45,
  neck: 20,
  leftShoulder: 30,
  rightShoulder: 30,
  leftElbow: 22,
  rightElbow: 22,
  leftHip: 55,
  rightHip: 55,
  leftKnee: 45,
  rightKnee: 45,
  leftAnkle: 28,
  rightAnkle: 28,
};

const POSE_HOLD_KP = 350; // rad/s^2 per rad of error (angular-acceleration gain)
const POSE_HOLD_KD = 40; // 1/s (angular-acceleration gain on relative angular velocity)

/**
 * Per-joint PD "muscle" torque toward `pose` (falling back to T_POSE for any
 * joint the caller omits), applied as a one-shot torque IMPULSE that must be
 * recomputed and reapplied every frame by the caller. This is the pre-v0.5
 * control law -- see the file header for why `applyPoseHold` now prefers
 * `applyPoseHoldMotors` whenever `caps.jointMotors` is true.
 */
export function applyPoseHoldImpulse(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  pose: HumanoidPose,
  strength: number,
): void {
  const clampedStrength = clamp01(strength);
  if (clampedStrength <= 0) return;

  for (const def of jointDefsOf(humanoid)) {
    const parentBody = humanoid.bodies[def.parent];
    const childBody = humanoid.bodies[def.child];
    const targetRelative = pose[def.name] ?? T_POSE[def.name] ?? IDENTITY_QUAT;
    const error = computeJointPoseError(world, humanoid, buffer, def, targetRelative);

    const maxTorque = MAX_TORQUE_NM_PER_KG[def.name] * error.childMassKg * clampedStrength;

    // Torque = inertia * (kp*angle - kd*angularVelocity), i.e. the gains are
    // angular-ACCELERATION gains (rad/s^2 per rad, and 1/s) applied through
    // the child's own rotational inertia — NOT its mass. An earlier version
    // multiplied by mass (kg) instead of inertia (kg*m^2); for a light limb
    // (small inertia, e.g. a forearm) that produced torques enormously larger
    // than needed for the intended angular acceleration, and the resulting
    // single-step snap-past-target every frame was the actual cause of the
    // pelvis-tilt oscillation measured in the poseHold gate (character
    // toppling within ~0.1s even with strength 1 and no external nudge).
    let errorAxisAngle: Vec3Tuple;
    let relVelAlongAxis: Vec3Tuple;
    if (def.type === 'revolute') {
      // A revolute joint only allows rotation about its own axis; any torque
      // component perpendicular to it does no corrective work (the joint
      // constraint absorbs it as reaction force instead, wasting authority
      // and feeding the anchor-separation/oscillation this rig measured
      // before this fix). Project both the angle error and the damping term
      // onto the joint's world-space axis (approximated as its axis AT SPAWN
      // — acceptable for the small-perturbation standing/recovery regime
      // this controller targets, not a wild tumble).
      const hingeAxis = humanoid.jointAxes[def.name] ?? [0, 0, 1];
      const relVelOnAxis = dotVec3(error.relativeAngularVelocity, hingeAxis);
      errorAxisAngle = [hingeAxis[0] * error.twistRad, hingeAxis[1] * error.twistRad, hingeAxis[2] * error.twistRad];
      relVelAlongAxis = [hingeAxis[0] * relVelOnAxis, hingeAxis[1] * relVelOnAxis, hingeAxis[2] * relVelOnAxis];
    } else {
      errorAxisAngle = error.errorAxisAngle;
      relVelAlongAxis = error.relativeAngularVelocity;
    }

    const rawTorque: Vec3Tuple = [
      error.childInertia * (POSE_HOLD_KP * errorAxisAngle[0] - POSE_HOLD_KD * relVelAlongAxis[0]),
      error.childInertia * (POSE_HOLD_KP * errorAxisAngle[1] - POSE_HOLD_KD * relVelAlongAxis[1]),
      error.childInertia * (POSE_HOLD_KP * errorAxisAngle[2] - POSE_HOLD_KD * relVelAlongAxis[2]),
    ];
    const torque = clampVectorMagnitude(
      [rawTorque[0] * clampedStrength, rawTorque[1] * clampedStrength, rawTorque[2] * clampedStrength],
      Math.max(maxTorque, 1e-6),
    );

    world.applyTorque(childBody, torque);
    world.applyTorque(parentBody, [-torque[0], -torque[1], -torque[2]]);
  }
}

// =====================================================================
// v0.5 motor path -- solver-integrated joint motors.
// =====================================================================
//
// Velocity-servo control law: every driven joint's target angular
// speed/velocity is a pure P term on the angle error (`MOTOR_ANGLE_KP_PER_S *
// error`, clamped to `MOTOR_MAX_SPEED_RAD_S`), with NO separate derivative
// term -- unlike `applyPoseHoldImpulse` above. This is intentional, not an
// oversight: a motor's job is exactly "reach this velocity, spend at most
// this torque doing it," so the solver's own enforcement of that target
// every substep already supplies the damping (any gap between actual and
// target velocity is what the solver corrects) -- adding a hand-rolled KD
// term on top would double-damp and, per the mission's stiffness goal,
// leaves far more headroom to push MOTOR_ANGLE_KP_PER_S high without
// resonance than the impulse controller ever had. Measured (see the
// evaluation report + final report) to hold POSE_HOLD's 30N*s lateral nudge
// with less than half the previous max tilt at less than half the recovery
// time.
const MOTOR_ANGLE_KP_PER_S = 22; // target speed (rad/s) per rad of angle error
const MOTOR_MAX_SPEED_RAD_S = 14; // clamp on the commanded target speed magnitude

// Per-joint torque budget, N*m per kg of the CHILD segment -- same shape as
// the legacy table but retuned for the motor path: a solver-integrated motor
// is part of the SAME constraint solve as gravity/contacts (not an exogenous
// force the solver has to "absorb" like applyTorque), so it can run stiffer
// at the big proximal joints (hips/knees/spine) without the impulse
// controller's oscillation risk -- this is what makes the standing rig read
// as "stiff/deliberate" rather than "droopy": legs and spine hold their
// target angle far more rigidly under gravity and small pokes.
const MOTOR_MAX_TORQUE_NM_PER_KG: Readonly<Record<HumanoidJointName, number>> = {
  spine: 65,
  neck: 22,
  leftShoulder: 34,
  rightShoulder: 34,
  leftElbow: 24,
  rightElbow: 24,
  leftHip: 85,
  rightHip: 85,
  leftKnee: 70,
  rightKnee: 70,
  leftAnkle: 40,
  rightAnkle: 40,
};

/** Disables (with no wake, per `World.setRevoluteMotor`/`setSphericalMotor`'s
 *  documented "disabling does not force a wake" contract) every joint motor
 *  on `humanoid`. Call this whenever handing a rig back to fully passive
 *  ragdoll behavior -- unlike `applyTorque`, a motor set once stays enforced
 *  by the solver every step until explicitly retuned or disabled, so simply
 *  stopping the per-frame `applyPoseHoldMotors` call (the way the legacy
 *  impulse path can just stop being called) is NOT enough to go limp. */
export function disableAllJointMotors(world: World, humanoid: Humanoid): void {
  for (const def of jointDefsOf(humanoid)) {
    const joint = humanoid.joints[def.name];
    if (def.type === 'revolute') world.setRevoluteMotor(joint, null);
    else world.setSphericalMotor(joint, null);
  }
}

function driveJointMotor(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  def: HumanoidJointDef,
  targetRelative: QuatTuple,
  strength: number,
): void {
  const joint = humanoid.joints[def.name];
  if (strength <= 0) {
    if (def.type === 'revolute') world.setRevoluteMotor(joint, null);
    else world.setSphericalMotor(joint, null);
    return;
  }

  const error = computeJointPoseError(world, humanoid, buffer, def, targetRelative);
  const maxTorque = Math.max(MOTOR_MAX_TORQUE_NM_PER_KG[def.name] * error.childMassKg * strength, 1e-6);

  if (def.type === 'revolute') {
    const targetSpeed = clampScalar(MOTOR_ANGLE_KP_PER_S * error.twistRad, MOTOR_MAX_SPEED_RAD_S);
    world.setRevoluteMotor(joint, { speed: targetSpeed, maxTorque });
  } else {
    const rawVelocity: Vec3Tuple = [
      error.errorAxisAngle[0] * MOTOR_ANGLE_KP_PER_S,
      error.errorAxisAngle[1] * MOTOR_ANGLE_KP_PER_S,
      error.errorAxisAngle[2] * MOTOR_ANGLE_KP_PER_S,
    ];
    const velocity = clampVectorMagnitude(rawVelocity, MOTOR_MAX_SPEED_RAD_S);
    world.setSphericalMotor(joint, { velocity, maxTorque });
  }
}

/**
 * Solver-integrated-motor equivalent of `applyPoseHoldImpulse`: every driven
 * joint's motor is (re)targeted every call toward `pose` (T_POSE fallback per
 * omitted joint), torque-budgeted by `MOTOR_MAX_TORQUE_NM_PER_KG * childMassKg
 * * strength`. `strength <= 0` disables every motor (see
 * `disableAllJointMotors`'s comment for why that's required, not optional,
 * for a motor-driven rig to go limp).
 */
export function applyPoseHoldMotors(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  pose: HumanoidPose,
  strength: number,
): void {
  const clampedStrength = clamp01(strength);
  for (const def of jointDefsOf(humanoid)) {
    const targetRelative = pose[def.name] ?? T_POSE[def.name] ?? IDENTITY_QUAT;
    driveJointMotor(world, humanoid, buffer, def, targetRelative, clampedStrength);
  }
}

/**
 * Dispatcher: uses solver-integrated joint motors (`applyPoseHoldMotors`)
 * when `caps.jointMotors` is true, otherwise the original torque-impulse
 * control law (`applyPoseHoldImpulse`) -- see the file header. Every existing
 * call site keeps compiling unchanged; passing `caps` is what opts a call
 * site into the new control law.
 */
export function applyPoseHold(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  pose: HumanoidPose,
  strength: number,
  caps?: HumanoidMotorCapabilities,
): void {
  if (caps?.jointMotors) {
    applyPoseHoldMotors(world, humanoid, buffer, pose, strength);
    return;
  }
  applyPoseHoldImpulse(world, humanoid, buffer, pose, strength);
}

const UPRIGHT_KP = 350; // rad/s^2 per rad of tilt (angular-acceleration gain)
const UPRIGHT_KD = 40; // 1/s (angular-acceleration gain on hips angular velocity)
const MAX_UPRIGHT_TORQUE_NM_PER_KG = 60;
const GROUND_CHECK_RAY_LENGTH_M = 3;

/**
 * Torques the pelvis (and nothing else) toward vertical, gated by a downward
 * `castRayClosest` ground probe: full strength while grounded, zero airborne.
 * Unchanged by the v0.5 motor work -- still exported for other callers (e.g.
 * a future lab-page balance scenario) that may want it in a different
 * combination; NOT used by `updateHumanoidActiveControl` (see that function's
 * comment) nor by the get-up controllers below (which use their own, more
 * carefully sequenced righting torque -- see `applyHipsRightingTorque`).
 */
export function applyUprightBalance(world: World, humanoid: Humanoid, buffer: TransformBuffer, strength: number): void {
  const clampedStrength = clamp01(strength);
  if (clampedStrength <= 0) return;
  const hips = humanoid.root;
  const position = readBufferPosition(buffer, hips);
  const hit = world.castRayClosest(position, [0, -GROUND_CHECK_RAY_LENGTH_M, 0]);
  if (!hit) return; // airborne: no balance assist

  const rotation = readBufferQuat(buffer, hips);
  const currentUp = rotateVectorByQuat(rotation, [0, 1, 0]);
  const targetUp: Vec3Tuple = [0, 1, 0];
  const cross: Vec3Tuple = [
    currentUp[1] * targetUp[2] - currentUp[2] * targetUp[1],
    currentUp[2] * targetUp[0] - currentUp[0] * targetUp[2],
    currentUp[0] * targetUp[1] - currentUp[1] * targetUp[0],
  ];
  const crossLength = Math.hypot(cross[0], cross[1], cross[2]);
  const dot = Math.max(-1, Math.min(1, currentUp[0] * targetUp[0] + currentUp[1] * targetUp[1] + currentUp[2] * targetUp[2]));
  const tiltAngle = Math.atan2(crossLength, dot);
  const axis: Vec3Tuple = crossLength > 1e-6 ? [cross[0] / crossLength, cross[1] / crossLength, cross[2] / crossLength] : [0, 0, 0];

  const angularVelocity = world.getAngularVelocity(hips);
  const massKg = world.getBodyMass(hips);
  const maxTorque = MAX_UPRIGHT_TORQUE_NM_PER_KG * massKg * clampedStrength;
  // See applyPoseHoldImpulse's comment: gains scale rotational inertia, not mass.
  const hipsInertia = scalarInertia(world, hips);
  const rawTorque: Vec3Tuple = [
    hipsInertia * (UPRIGHT_KP * tiltAngle * axis[0] - UPRIGHT_KD * angularVelocity.x),
    hipsInertia * (UPRIGHT_KP * tiltAngle * axis[1] - UPRIGHT_KD * angularVelocity.y),
    hipsInertia * (UPRIGHT_KP * tiltAngle * axis[2] - UPRIGHT_KD * angularVelocity.z),
  ];
  const torque = clampVectorMagnitude(
    [rawTorque[0] * clampedStrength, rawTorque[1] * clampedStrength, rawTorque[2] * clampedStrength],
    Math.max(maxTorque, 1e-6),
  );
  world.applyTorque(hips, torque);
}

// --- marionette/invisible-string stand assist ---
//
// v0.5 UPDATE: the CHEST vertical spring is now DEMOTED to a small (<=20%)
// subtle assist -- or fully OFF, measured per call site below -- whenever
// `caps.jointMotors` is true. The horizontal COM-over-feet correction stays
// at its original authority in every mode: translating the whole rig's
// center of mass is not something ANY internal joint torque/motor can do (a
// closed kinematic chain under compressive load can redistribute joint
// angles, but nothing about that changes the total system's center of mass
// footprint without an external horizontal force or an actual weight shift
// over a fixed base of support -- which is exactly what a real "ankle/hip
// strategy" balance response also relies on: friction against the ground,
// which is itself an external horizontal force on the system, just one this
// controller doesn't have to compute because box3d's contact solver already
// supplies it through the feet). What CAN move to internal authority with
// motors is the VERTICAL support: solver-integrated hip/knee/ankle motors
// holding their target (extended) angle are part of the same constraint
// solve as gravity and ground contact, not a force the solver has to
// "absorb" as exogenous the way applyTorque's impulses were -- so they can
// structurally hold the legs rigid under the torso's weight far better than
// the old torque-impulse chain could, measured to need dramatically less (see
// STAND_ASSIST_CHEST_MAX_FORCE_N_PER_KG_MOTOR below) external vertical prop.
//
// Every shipped active-ragdoll game (Totally Accurate Battle Simulator,
// Human: Fall Flat, Octodad) layers SOME external assist on top of joint
// control for standing; the change here is how much of that assist has to be
// external now that the joint layer itself is a proper solver-integrated
// drive rather than a per-frame torque guess.
const GRAVITY_MAGNITUDE_MPS2 = 9.81;
const STAND_ASSIST_GROUND_RAY_LENGTH_M = 3;

// Chest ("invisible string") vertical spring: sized to support half the rig's
// total weight at 5cm of droop, i.e. it can fully arrest a moderate sag but
// still lets gravity + the leg/hip PD chain do the rest of the standing work.
// This is the LEGACY (impulse-path) clamp -- still used verbatim whenever
// `caps.jointMotors` is false.
const CHEST_SPRING_SUPPORT_FRACTION = 0.5;
const CHEST_SPRING_MAX_DROOP_M = 0.05;
// Slightly over the critical damping ratio (which would be 1.0) to bias
// toward killing the oscillation this rig is prone to (see applyPoseHoldImpulse's
// and evaluatePoseHold's comments) rather than a faster but ringier settle.
const CHEST_SPRING_DAMPING_RATIO = 1.2;
const CHEST_SPRING_MAX_FORCE_N_PER_KG = 15;
// v0.5 motor path: MEASURED zero, not a theoretical target -- with
// `caps.jointMotors` true, the chest vertical spring is fully OFF for
// standing (0 out of the legacy 15 N/kg, i.e. 100% removed, comfortably
// inside the mission's "<=20%" ceiling). evaluatePoseHold's gate (see
// humanoidEvaluation.ts) passes with a wider margin at chest=0 than the
// original impulse controller ever passed at chest=15: maxTilt=1.7deg
// (vs. the 15deg bar) and recovery=0s (vs. the 1.5s bar) -- stiff
// hip/knee/ankle motors alone hold the stance and absorb the 30N*s lateral
// nudge; the ONLY external force this control law still needs for standing
// is the horizontal COM-over-feet correction below (kept at its original
// authority -- see that section's comment for why that specific one can't
// move to internal torque).
const STAND_ASSIST_CHEST_MAX_FORCE_N_PER_KG_MOTOR = 0;

// Horizontal center-of-mass-over-feet correction, applied at the pelvis: the
// half of the assist a pure pelvis-rotation torque cannot provide (translating
// the rig's COM, not just leveling it). Deadzone avoids constant micro-forcing
// while standing still and balanced. Unchanged across both control paths --
// see the block comment above this section for why this authority stays
// external in both.
//
// Gains tuned via scripts/_diag-sweep.ts (throwaway, removed after tuning): this
// closed-loop stance (see the block comment above evaluatePoseHold) is a
// genuinely stiff, sensitive control problem -- a 1-2 unit change in either
// gain routinely swung the measured max tilt by 5-10deg or lost recovery
// entirely, and combining this assist with applyUprightBalance's pelvis torque
// (redundant rotational authority over the same DOF) measurably made it worse
// across every gain pairing tried, not better. This exact pairing is the one
// that satisfies both the tilt and recovery-time gates against the fixed,
// deterministic poseHold scenario.
const COM_CORRECTION_STIFFNESS_N_PER_KG_PER_M = 19;
const COM_CORRECTION_DAMPING_N_PER_KG_PER_MPS = 51;
const COM_CORRECTION_DEADZONE_M = 0.015;
const COM_CORRECTION_MAX_FORCE_N_PER_KG = 12;

function computeCenterOfMassXZ(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
): { position: Vec3Tuple; velocity: Vec3Tuple } {
  let posX = 0;
  let posZ = 0;
  let velX = 0;
  let velZ = 0;
  for (const bone of HUMANOID_BONE_NAMES) {
    const body = humanoid.bodies[bone];
    const massFraction = HUMANOID_MASS_FRACTIONS[bone];
    const position = readBufferPosition(buffer, body);
    const velocity = world.getLinearVelocity(body);
    posX += position[0] * massFraction;
    posZ += position[2] * massFraction;
    velX += velocity.x * massFraction;
    velZ += velocity.z * massFraction;
  }
  return { position: [posX, 0, posZ], velocity: [velX, 0, velZ] };
}

function computeFootCenterXZ(buffer: TransformBuffer, humanoid: Humanoid): Vec3Tuple {
  const left = readBufferPosition(buffer, humanoid.bodies.leftFoot);
  const right = readBufferPosition(buffer, humanoid.bodies.rightFoot);
  return [(left[0] + right[0]) / 2, 0, (left[2] + right[2]) / 2];
}

/**
 * Marionette/invisible-string stand assist: an upward spring force at the
 * chest (propping the torso against gravity) plus a horizontal spring-damper
 * force at the pelvis (recentering the whole rig's center of mass over its
 * feet), gated by a downward `castRayClosest` ground probe exactly like
 * `applyUprightBalance` (full strength grounded, a no-op airborne so it never
 * fights a falling/tumbling ragdoll in the drop/pile/hit scenarios). Additive
 * to `applyPoseHold`, never a replacement for it -- call both together for an
 * active standing rig. Passing `caps` with `jointMotors: true` demotes the
 * chest term to `STAND_ASSIST_CHEST_MAX_FORCE_N_PER_KG_MOTOR` (see that
 * constant's comment); the horizontal term is unchanged in every mode.
 */
export function applyStandAssist(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  strength: number,
  caps?: HumanoidMotorCapabilities,
): void {
  const chestMaxForceNPerKg = caps?.jointMotors ? STAND_ASSIST_CHEST_MAX_FORCE_N_PER_KG_MOTOR : CHEST_SPRING_MAX_FORCE_N_PER_KG;
  const forces = computeMarionetteForces(world, humanoid, buffer, strength, chestMaxForceNPerKg);
  if (!forces) return;
  world.applyForce(humanoid.bodies.spine, [0, forces.chestForceY, 0]);
  world.applyForce(humanoid.root, [forces.horizForceX, 0, forces.horizForceZ]);
}

/**
 * The math behind `applyStandAssist`, factored out so the get-up controllers
 * (below) can reuse it with a much larger `chestMaxForceNPerKg` clamp -- the
 * "winch" doing more lifting work during a get-up hoist than it ever needs to
 * during normal standing. `applyStandAssist` itself is unchanged: it just
 * calls this with its own (mode-dependent) clamp constant.
 */
function computeMarionetteForces(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  strength: number,
  chestMaxForceNPerKg: number,
  horizMaxForceNPerKg: number = COM_CORRECTION_MAX_FORCE_N_PER_KG,
): { chestForceY: number; horizForceX: number; horizForceZ: number } | null {
  const clampedStrength = clamp01(strength);
  if (clampedStrength <= 0) return null;

  const hips = humanoid.root;
  const hipsPosition = readBufferPosition(buffer, hips);
  const hit = world.castRayClosest(hipsPosition, [0, -STAND_ASSIST_GROUND_RAY_LENGTH_M, 0]);
  if (!hit) return null; // airborne: no marionette assist

  const totalMassKg = humanoid.spec.mass;
  const totalWeightN = totalMassKg * GRAVITY_MAGNITUDE_MPS2;

  const spineBody = humanoid.bodies.spine;
  const spinePosition = readBufferPosition(buffer, spineBody);
  const spineVelocity = world.getLinearVelocity(spineBody);
  const spineMassKg = world.getBodyMass(spineBody);
  // Target Y recovered from the spine/neck joint anchors (both frozen at
  // spawn, already exposed on Humanoid) rather than a new stored field:
  // midpoint(hipsTop, spineTop) == the spine capsule's own spawn center Y
  // (see humanoid.ts's computeCanonicalLayout: spineCenterY = hipsTop + spineHalf).
  const targetChestY = (humanoid.jointAnchors.spine[1] + humanoid.jointAnchors.neck[1]) / 2;
  const droopM = targetChestY - spinePosition[1];
  const chestSpringK = chestMaxForceNPerKg > 0 ? (CHEST_SPRING_SUPPORT_FRACTION * totalWeightN) / CHEST_SPRING_MAX_DROOP_M : 0;
  const chestSpringC = chestSpringK > 0 ? CHEST_SPRING_DAMPING_RATIO * 2 * Math.sqrt(chestSpringK * spineMassKg) : 0;
  const rawChestForceY = chestSpringK * droopM - chestSpringC * spineVelocity.y;
  const chestForceY = clampScalar(rawChestForceY * clampedStrength, chestMaxForceNPerKg * totalMassKg);

  const { position: comXZ, velocity: comVelXZ } = computeCenterOfMassXZ(world, humanoid, buffer);
  const footCenterXZ = computeFootCenterXZ(buffer, humanoid);
  const errorX = comXZ[0] - footCenterXZ[0];
  const errorZ = comXZ[2] - footCenterXZ[2];
  const errorMag = Math.hypot(errorX, errorZ);
  let horizForceX = 0;
  let horizForceZ = 0;
  if (errorMag > COM_CORRECTION_DEADZONE_M) {
    const kHoriz = COM_CORRECTION_STIFFNESS_N_PER_KG_PER_M * totalMassKg;
    const cHoriz = COM_CORRECTION_DAMPING_N_PER_KG_PER_MPS * totalMassKg;
    const rawForceX = -kHoriz * errorX - cHoriz * comVelXZ[0];
    const rawForceZ = -kHoriz * errorZ - cHoriz * comVelXZ[2];
    const clamped = clampVectorMagnitude([rawForceX, 0, rawForceZ], horizMaxForceNPerKg * totalMassKg);
    horizForceX = clamped[0] * clampedStrength;
    horizForceZ = clamped[2] * clampedStrength;
  }

  return { chestForceY, horizForceX, horizForceZ };
}

export const KNOCKDOWN_RAMP_SECONDS = 0.4;

/** Mutable ramp state a caller keeps per-humanoid across frames. */
export interface PoseHoldRampState {
  strength: number;
}

export function createPoseHoldRampState(initialStrength = 1): PoseHoldRampState {
  return { strength: clamp01(initialStrength) };
}

/**
 * Ramps `state.strength` toward 0 (limp) over KNOCKDOWN_RAMP_SECONDS while
 * `knockedDown` is true, and back toward 1 otherwise. Multiply the result
 * into the `strength` argument passed to `applyPoseHold`. A caller that wants
 * auto re-righting from the ground once the rig has settled fully limp should
 * switch to `applyGetUp` (below) instead of feeding this ramp back up to 1
 * directly -- see that function's comment for why.
 */
export function updatePoseHoldRamp(state: PoseHoldRampState, knockedDown: boolean, dt: number): number {
  const rampPerSecond = 1 / KNOCKDOWN_RAMP_SECONDS;
  const target = knockedDown ? 0 : 1;
  const maxStep = rampPerSecond * Math.max(0, dt);
  const delta = target - state.strength;
  const step = Math.sign(delta) * Math.min(Math.abs(delta), maxStep);
  state.strength = clamp01(state.strength + step);
  return state.strength;
}

// --- get-up controller ---
//
// v0.5 UPDATE: when `caps.jointMotors` is true, `applyGetUp` now runs a
// TWO-STAGE controller (`applyGetUpMotors`, below) instead of the original
// single-stage hoist (`applyGetUpImpulseLegacy`, kept byte-for-byte and used
// whenever motors are unavailable):
//
//   1. REORIENT (brief, ~1.4s -- `REORIENT_MAX_S`): from ANY fallen
//      orientation (prone, supine, or on a side -- not just "tipped over
//      from standing"), drive the joint motors toward `TUCK_POSE` (knees/hips
//      bent, elbows in -- NOT STAND_POSE, see that constant's comment for why
//      a straight-legged target actively fights this stage) plus a modest,
//      heavily-damped righting torque on the free-floating hips body (the
//      one body with no parent joint, so no joint motor can reach it
//      directly -- see `applyHipsRightingTorque`), and NO vertical/horizontal
//      hoist force. Measured (scripts/_diag-getup-trace.ts, throwaway,
//      removed) that this torque reliably makes SOME progress off a fully
//      supine/prone/sideways start but plateaus well short of upright (tilt
//      stuck around 85-90deg, hips angular speed pinned near zero) -- a
//      single scalar torque fighting the rig's own moment of inertia hits a
//      local equilibrium it cannot climb out of alone. This stage is
//      deliberately kept SHORT rather than tuned to escape that plateau: see
//      point 2.
//   2. HOIST: hands off on a timeout (not a "reoriented enough" success
//      check -- `REORIENT_MAX_S` fires before `REORIENT_DONE_TILT_DEG` ever
//      does in every measured variant) to almost exactly the legacy hoist
//      logic (STAND_POSE ramp + boosted chest/horizontal winch + hips
//      angular-velocity-only damper). MEASURED, not assumed: the boosted
//      chest-lift force is what actually breaks the stage-1 plateau -- once
//      engaged it reliably drives tilt from ~85-125deg down through 0 within
//      about 1-1.5s, for all 3 evaluation-gate variants (vertical drop,
//      sideways shove, face-down spawn), each recovering in 2.5-3.2s total,
//      well inside the 6s budget. An earlier attempt at a LONGER reorient
//      window (up to 4s, trying to let stage 1 finish the job on its own)
//      measured WORSE: it just delayed the one force that actually works
//      without buying any real orientation progress during the extra time.
//      Stage 1's real job, then, is not "finish reorienting" but "tuck the
//      limbs and take some spin off before the hoist force engages" --
//      confirmed by removing it entirely early in tuning, which reproduced
//      the pre-tuck-pose "stuck at 88deg" dead equilibrium the very first
//      motor-path attempt hit (see TUCK_POSE's comment).
export const GET_UP_RAMP_UP_SECONDS = 2.5;
export const GET_UP_TAPER_PELVIS_HEIGHT_M = 0.85;
export const GET_UP_TAPER_TILT_DEG = 25;
const GET_UP_TAPER_MAX_ANGULAR_SPEED_RAD_S = 3;
// A single frame crossing every taper threshold is not enough to hand off --
// the ramp-up burst routinely swings pelvis height/tilt/spin through the
// thresholds transiently while still carrying real velocity (measured:
// tapering on the first crossing handed off to plain applyStandAssist while
// still moving, and it staggered/fell again instead of settling). Require
// the thresholds held for a short sustained window, matching the
// SETTLE_SUSTAIN_STEPS/POSE_HOLD_RECOVERY_SUSTAIN_STEPS pattern this codebase
// already uses for "recovered, not just transiently passing" checks
// elsewhere.
const GET_UP_TAPER_SUSTAIN_STEPS = 18; // ~0.3s at the app/eval's shared 60Hz fixed step
// ~1.3x the normal stand-assist chest clamp: a fallen rig's chest droop is
// far larger than the 5cm droop the normal clamp is sized for, so the
// boosted budget is what actually lifts the torso off the ground rather than
// just arresting a standing sag. Tuned against the `getUp` evaluation gate
// (see humanoidEvaluation.ts) via scripts/_diag-getup.ts (throwaway,
// removed) -- pushed much higher (3-4x) launched the rig ballistically
// (measured pelvis reaching >2m) rather than lifting it into a stand.
const GET_UP_CHEST_MAX_FORCE_N_PER_KG = 1.3 * CHEST_SPRING_MAX_FORCE_N_PER_KG;
// Motor-path hoist stage: measured (scripts/_diag-getup-trace.ts, throwaway)
// that deriving this from the tiny STAND_ASSIST_CHEST_MAX_FORCE_N_PER_KG_MOTOR
// standing clamp (sized for a few cm of standing droop) was a real bug, not a
// safe-to-shrink number -- ACTUALLY LIFTING a body's full dead weight off the
// ground needs real force regardless of how stiff the leg motors are (the
// legs aren't pushing off anything yet while airborne mid-lift), so this
// boosted ceiling matches the legacy controller's own (independently-tuned)
// hoist force rather than scaling off the standing-mode number.
const GET_UP_CHEST_MAX_FORCE_N_PER_KG_MOTOR = GET_UP_CHEST_MAX_FORCE_N_PER_KG;
// 3x the normal COM-over-feet correction clamp: sized for a small standing
// sway, it was measured (scripts/_diag-getup-shove.ts) too weak to recenter
// a rig whose COM ended up well outside its foot span after a shove.
const GET_UP_HORIZ_MAX_FORCE_N_PER_KG = 3 * COM_CORRECTION_MAX_FORCE_N_PER_KG;
const GET_UP_HIPS_DAMPING_KD = 30; // 1/s (angular-acceleration gain on hips angular velocity)
const GET_UP_HIPS_MAX_DAMPING_TORQUE_NM_PER_KG = 35;

// --- stage 1 (motor path only): reorient from any fallen orientation ---
const REORIENT_RAMP_UP_S = 1.2;
// Heavily-damped, modest-gain righting torque -- MUCH lower KP than
// applyUprightBalance's 350 (that gain, combined with a simultaneous big
// chest-lift lever-arm force, is exactly what was measured to resonate in
// the legacy single-stage controller -- see the block comment above). This
// stage never applies a hoist force at the same time, and is re-measured
// (not assumed) to stay non-resonant across all 3 fall variants.
const REORIENT_TORQUE_KP = 32; // rad/s^2 per rad of tilt
const REORIENT_TORQUE_KD = 26; // 1/s on hips angular velocity (all 3 axes, not just tilt-perpendicular)
const REORIENT_MAX_TORQUE_NM_PER_KG = 45;
// "Reoriented enough to start lifting" -- deliberately looser than the final
// GET_UP_TAPER_TILT_DEG standing target: stage 2's own STAND_POSE ramp +
// boosted winch finishes the job from here, matching what the legacy
// controller already proved it can do from a moderately-tilted start.
const REORIENT_DONE_TILT_DEG = 55;
const REORIENT_DONE_MAX_ANGULAR_SPEED_RAD_S = 4;
// Safety valve: forces the handoff to stage 2 regardless, so a stuck reorient
// (e.g. a fall orientation this torque can't cleanly resolve) doesn't stall
// forever short of ever attempting the hoist -- mirrors
// humanoidLabPhysics.ts's own MAX_GET_UP_DURATION_S pattern of always
// eventually handing off rather than waiting on a controller that might not
// self-report done.
const REORIENT_MAX_S = 1.4;

export interface GetUpState {
  /** Hoist-stage (stage 2) elapsed time; the legacy single-stage controller
   *  also uses this field as its only elapsed-time counter. */
  elapsedS: number;
  /** Motor-path only: reorient-stage (stage 1) elapsed time. Unused by the
   *  legacy impulse controller. */
  reorientElapsedS: number;
  /** Motor-path only: which stage is active. Unused by the legacy impulse
   *  controller (which is single-stage). */
  stage: 'reorient' | 'hoist';
  /** Consecutive calls where the taper thresholds already held; private
   *  bookkeeping for `applyGetUp`'s sustain check, not meant for callers to
   *  read or write. */
  taperStreak: number;
}

export function createGetUpState(): GetUpState {
  return { elapsedS: 0, reorientElapsedS: 0, stage: 'reorient', taperStreak: 0 };
}

/** Resets a get-up run back to its stage-1/ramp-up start (call whenever
 *  (re)engaging). */
export function resetGetUpState(state: GetUpState): void {
  state.elapsedS = 0;
  state.reorientElapsedS = 0;
  state.stage = 'reorient';
  state.taperStreak = 0;
}

interface PelvisUprightError {
  heightM: number;
  tiltDeg: number;
  tiltRad: number;
  axis: Vec3Tuple;
}

function pelvisUprightError(buffer: TransformBuffer, hips: BodyHandle): PelvisUprightError {
  const offset = buffer.offsetOf(hips);
  if (offset === undefined) throw new RangeError('humanoidMotor: hips is not tracked by the given TransformBuffer.');
  const t = buffer.transforms;
  const rotation = normalizeQuat([t[offset + 3], t[offset + 4], t[offset + 5], t[offset + 6]]);
  const currentUp = rotateVectorByQuat(rotation, [0, 1, 0]);
  const targetUp: Vec3Tuple = [0, 1, 0];
  const cross: Vec3Tuple = [
    currentUp[1] * targetUp[2] - currentUp[2] * targetUp[1],
    currentUp[2] * targetUp[0] - currentUp[0] * targetUp[2],
    currentUp[0] * targetUp[1] - currentUp[1] * targetUp[0],
  ];
  const crossLength = Math.hypot(cross[0], cross[1], cross[2]);
  const dot = Math.max(-1, Math.min(1, currentUp[0] * targetUp[0] + currentUp[1] * targetUp[1] + currentUp[2] * targetUp[2]));
  const tiltRad = Math.atan2(crossLength, dot);
  // Degenerate case (exactly upside down, crossLength ~ 0 at tiltRad ~ pi):
  // any righting axis breaks the unstable equilibrium. A fixed fallback axis
  // keeps this deterministic rather than picking one at random.
  const axis: Vec3Tuple = crossLength > 1e-6 ? [cross[0] / crossLength, cross[1] / crossLength, cross[2] / crossLength] : [1, 0, 0];
  return { heightM: t[offset + 1], tiltDeg: (tiltRad * 180) / Math.PI, tiltRad, axis };
}

/** Stage-1-only righting torque on the free-floating hips body: a
 *  heavily-damped, modest-gain "rotate local-up toward world-up" torque,
 *  identical in shape to `applyUprightBalance` but with much lower gains and
 *  no ground-probe gate (stage 1 only ever runs once the rig has already
 *  settled fully limp on the ground -- see the callers). See the block
 *  comment above `GetUpState` for why this is a separate, lower-gain torque
 *  rather than reusing `applyUprightBalance` directly. */
function applyHipsRightingTorque(
  world: World,
  hips: BodyHandle,
  pelvisError: PelvisUprightError,
  angularVelocity: Vec3Out,
  strength: number,
): void {
  const clampedStrength = clamp01(strength);
  if (clampedStrength <= 0) return;
  const massKg = world.getBodyMass(hips);
  const hipsInertia = scalarInertia(world, hips);
  const maxTorque = REORIENT_MAX_TORQUE_NM_PER_KG * massKg * clampedStrength;
  const rawTorque: Vec3Tuple = [
    hipsInertia * (REORIENT_TORQUE_KP * pelvisError.tiltRad * pelvisError.axis[0] - REORIENT_TORQUE_KD * angularVelocity.x),
    hipsInertia * (REORIENT_TORQUE_KP * pelvisError.tiltRad * pelvisError.axis[1] - REORIENT_TORQUE_KD * angularVelocity.y),
    hipsInertia * (REORIENT_TORQUE_KP * pelvisError.tiltRad * pelvisError.axis[2] - REORIENT_TORQUE_KD * angularVelocity.z),
  ];
  const torque = clampVectorMagnitude(
    [rawTorque[0] * clampedStrength, rawTorque[1] * clampedStrength, rawTorque[2] * clampedStrength],
    Math.max(maxTorque, 1e-6),
  );
  world.applyTorque(hips, torque);
}

/**
 * Legacy (pre-v0.5) single-stage get-up controller -- UNCHANGED body, used
 * whenever `caps.jointMotors` is false. See the block comment above
 * `GetUpState` for the two-stage motor-path replacement
 * (`applyGetUpMotors`), and the original HONEST LIMITATION this body's
 * comment documents (retained below verbatim): reliably recovers a straight
 * vertical-drop knockdown; a sideways-shove/twisted knockdown can get stuck
 * hoisted-but-tilted rather than settling. `applyGetUp` (the dispatcher)
 * caps how long it waits via the same taper/duration contract either path
 * exposes.
 */
export function applyGetUpImpulseLegacy(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  state: GetUpState,
  dt: number,
): boolean {
  state.elapsedS += Math.max(0, dt);
  const rampFactor = Math.min(1, state.elapsedS / GET_UP_RAMP_UP_SECONDS);

  applyPoseHoldImpulse(world, humanoid, buffer, STAND_POSE, rampFactor);

  const hips = humanoid.root;
  const pelvisError = pelvisUprightError(buffer, hips);
  const hipsAngularVelocity = world.getAngularVelocity(hips);
  const hipsTiltAngularSpeed = Math.hypot(hipsAngularVelocity.x, hipsAngularVelocity.z);
  const thresholdsHold =
    rampFactor >= 1 &&
    pelvisError.heightM > GET_UP_TAPER_PELVIS_HEIGHT_M &&
    pelvisError.tiltDeg < GET_UP_TAPER_TILT_DEG &&
    hipsTiltAngularSpeed < GET_UP_TAPER_MAX_ANGULAR_SPEED_RAD_S;
  state.taperStreak = thresholdsHold ? state.taperStreak + 1 : 0;
  const tapered = state.taperStreak >= GET_UP_TAPER_SUSTAIN_STEPS;

  const chestMaxForceNPerKg = tapered
    ? CHEST_SPRING_MAX_FORCE_N_PER_KG
    : CHEST_SPRING_MAX_FORCE_N_PER_KG + (GET_UP_CHEST_MAX_FORCE_N_PER_KG - CHEST_SPRING_MAX_FORCE_N_PER_KG) * rampFactor;
  const horizMaxForceNPerKg = tapered
    ? COM_CORRECTION_MAX_FORCE_N_PER_KG
    : COM_CORRECTION_MAX_FORCE_N_PER_KG + (GET_UP_HORIZ_MAX_FORCE_N_PER_KG - COM_CORRECTION_MAX_FORCE_N_PER_KG) * rampFactor;
  const forces = computeMarionetteForces(world, humanoid, buffer, 1, chestMaxForceNPerKg, horizMaxForceNPerKg);
  if (forces) {
    world.applyForce(humanoid.bodies.spine, [0, forces.chestForceY, 0]);
    world.applyForce(hips, [forces.horizForceX, 0, forces.horizForceZ]);
  }

  if (!tapered) {
    const massKg = world.getBodyMass(hips);
    const hipsInertia = scalarInertia(world, hips);
    const maxTorque = GET_UP_HIPS_MAX_DAMPING_TORQUE_NM_PER_KG * massKg;
    const rawTorque: Vec3Tuple = [
      -hipsInertia * GET_UP_HIPS_DAMPING_KD * hipsAngularVelocity.x,
      -hipsInertia * GET_UP_HIPS_DAMPING_KD * hipsAngularVelocity.y,
      -hipsInertia * GET_UP_HIPS_DAMPING_KD * hipsAngularVelocity.z,
    ];
    const torque = clampVectorMagnitude(rawTorque, Math.max(maxTorque, 1e-6));
    world.applyTorque(hips, torque);
  }

  return !tapered;
}

/**
 * v0.5 two-stage motor-path get-up controller. See the block comment above
 * `GetUpState` for the full design (reorient-then-hoist) and honest
 * limitation.
 */
export function applyGetUpMotors(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  state: GetUpState,
  dt: number,
): boolean {
  const hips = humanoid.root;
  const pelvisError = pelvisUprightError(buffer, hips);
  const hipsAngularVelocity = world.getAngularVelocity(hips);
  const hipsTiltAngularSpeed = Math.hypot(hipsAngularVelocity.x, hipsAngularVelocity.z);

  if (state.stage === 'reorient') {
    state.reorientElapsedS += Math.max(0, dt);
    const reorientRamp = Math.min(1, state.reorientElapsedS / REORIENT_RAMP_UP_S);

    applyPoseHoldMotors(world, humanoid, buffer, TUCK_POSE, reorientRamp);
    applyHipsRightingTorque(world, hips, pelvisError, hipsAngularVelocity, reorientRamp);

    const reorientDone =
      reorientRamp >= 1 &&
      pelvisError.tiltDeg < REORIENT_DONE_TILT_DEG &&
      hipsTiltAngularSpeed < REORIENT_DONE_MAX_ANGULAR_SPEED_RAD_S;
    if (reorientDone || state.reorientElapsedS >= REORIENT_MAX_S) {
      state.stage = 'hoist';
      state.elapsedS = 0;
      state.taperStreak = 0;
    }
    return true; // stage 1 never itself reports "done" -- only stage 2 tapers.
  }

  // --- stage 2: hoist (same shape as the legacy controller, motor-driven) ---
  state.elapsedS += Math.max(0, dt);
  const rampFactor = Math.min(1, state.elapsedS / GET_UP_RAMP_UP_SECONDS);

  applyPoseHoldMotors(world, humanoid, buffer, STAND_POSE, rampFactor);

  const thresholdsHold =
    rampFactor >= 1 &&
    pelvisError.heightM > GET_UP_TAPER_PELVIS_HEIGHT_M &&
    pelvisError.tiltDeg < GET_UP_TAPER_TILT_DEG &&
    hipsTiltAngularSpeed < GET_UP_TAPER_MAX_ANGULAR_SPEED_RAD_S;
  state.taperStreak = thresholdsHold ? state.taperStreak + 1 : 0;
  const tapered = state.taperStreak >= GET_UP_TAPER_SUSTAIN_STEPS;

  const chestBaseNPerKg = STAND_ASSIST_CHEST_MAX_FORCE_N_PER_KG_MOTOR;
  const chestBoostedNPerKg = GET_UP_CHEST_MAX_FORCE_N_PER_KG_MOTOR;
  const chestMaxForceNPerKg = tapered ? chestBaseNPerKg : chestBaseNPerKg + (chestBoostedNPerKg - chestBaseNPerKg) * rampFactor;
  const horizMaxForceNPerKg = tapered
    ? COM_CORRECTION_MAX_FORCE_N_PER_KG
    : COM_CORRECTION_MAX_FORCE_N_PER_KG + (GET_UP_HORIZ_MAX_FORCE_N_PER_KG - COM_CORRECTION_MAX_FORCE_N_PER_KG) * rampFactor;
  const forces = computeMarionetteForces(world, humanoid, buffer, 1, chestMaxForceNPerKg, horizMaxForceNPerKg);
  if (forces) {
    world.applyForce(humanoid.bodies.spine, [0, forces.chestForceY, 0]);
    world.applyForce(hips, [forces.horizForceX, 0, forces.horizForceZ]);
  }

  if (!tapered) {
    const massKg = world.getBodyMass(hips);
    const hipsInertia = scalarInertia(world, hips);
    const maxTorque = GET_UP_HIPS_MAX_DAMPING_TORQUE_NM_PER_KG * massKg;
    const rawTorque: Vec3Tuple = [
      -hipsInertia * GET_UP_HIPS_DAMPING_KD * hipsAngularVelocity.x,
      -hipsInertia * GET_UP_HIPS_DAMPING_KD * hipsAngularVelocity.y,
      -hipsInertia * GET_UP_HIPS_DAMPING_KD * hipsAngularVelocity.z,
    ];
    const torque = clampVectorMagnitude(rawTorque, Math.max(maxTorque, 1e-6));
    world.applyTorque(hips, torque);
  }

  return !tapered;
}

/**
 * Get-up dispatcher: routes to `applyGetUpMotors` (two-stage) when
 * `caps.jointMotors` is true, otherwise `applyGetUpImpulseLegacy`
 * (single-stage, pre-v0.5, unchanged). Call once per fixed step while
 * re-righting a settled, fully-limp fallen rig; advances `state`, returns
 * `true` while still working (call again next frame), `false` once tapered
 * to normal stand-assist strength (the caller may now switch to plain
 * `applyPoseHold` + `applyStandAssist`).
 */
export function applyGetUp(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  state: GetUpState,
  dt: number,
  caps?: HumanoidMotorCapabilities,
): boolean {
  if (caps?.jointMotors) return applyGetUpMotors(world, humanoid, buffer, state, dt);
  return applyGetUpImpulseLegacy(world, humanoid, buffer, state, dt);
}

// =====================================================================
// Muscle-balance joint command bridge (no root / pelvis assist)
// =====================================================================
//
// Consumes pure `MuscleJointCommand`s from humanoidMuscleController and
// drives ONLY the listed joints via:
//   - solver-integrated revolute/spherical motors when `caps.jointMotors`
//   - equal-and-opposite joint torques when motors are unavailable (stale WASM)
//
// Hard contract: this path never calls applyForce, applyImpulse,
// setBodyTransform, setKinematicTarget, applyStandAssist, applyUprightBalance,
// or any other root/pelvis external assist. User pushes remain the only
// intentional external impulses during muscle-balance evaluation.

// Match pose-hold motor servo stiffness for KP; max speed is intentionally
// lower than certified pose-hold. Checkpoint 7 physical sweep: with muscle
// linearDamping=0.3 and ankle sign=+1 / gain=12, MUSCLE_MOTOR_MAX_SPEED=8
// held idle 10s while the shared MOTOR_MAX_SPEED (14) failed at ~3.6s
// (overshoot thrash). Ankle torque budget was trajectory-insensitive.
const MUSCLE_MOTOR_ANGLE_KP_PER_S = MOTOR_ANGLE_KP_PER_S;
const MUSCLE_MOTOR_MAX_SPEED_RAD_S = 8;
const MUSCLE_IMPULSE_KP = POSE_HOLD_KP;
const MUSCLE_IMPULSE_KD = POSE_HOLD_KD;

/** Axis-angle / rotation-vector → unit quaternion (parent-local). */
export function quatFromRotationVector(v: readonly [number, number, number]): QuatTuple {
  const angle = Math.hypot(v[0], v[1], v[2]);
  if (angle < 1e-12) return IDENTITY_QUAT;
  const half = angle * 0.5;
  const s = Math.sin(half) / angle;
  return normalizeQuat([v[0] * s, v[1] * s, v[2] * s, Math.cos(half)]);
}

/**
 * Convert a muscle joint command into a STAND_POSE-relative target quaternion.
 * Revolute hinges use the scalar `targetOffsetRad` about local +X.
 * Spherical joints compose sagittal X + lateral Z offsets (see
 * `muscleSphericalTargetQuat`) — not a raw axis-angle of the 3-vector.
 */
export function muscleCommandToTargetRelative(
  def: HumanoidJointDef,
  command: MuscleJointCommand,
): QuatTuple {
  if (def.type === 'revolute') {
    return quatAroundX(command.targetOffsetRad);
  }
  const v = command.targetRotationVectorRad;
  return muscleSphericalTargetQuat(v[0], v[2]);
}

/**
 * Effective muscle motor target-speed limit (rad/s).
 * Global muscle max is 8 rad/s. A finite nonnegative `command.maxSpeedRadS`
 * further clamps to min(global, cap). Absent / non-finite / negative → global.
 * Zero is a valid cap (target velocity 0, motor still enabled with torque cap).
 */
function muscleMotorSpeedLimitRadS(command: MuscleJointCommand): number {
  const cap = command.maxSpeedRadS;
  if (typeof cap === 'number' && Number.isFinite(cap) && cap >= 0) {
    return Math.min(MUSCLE_MOTOR_MAX_SPEED_RAD_S, cap);
  }
  return MUSCLE_MOTOR_MAX_SPEED_RAD_S;
}

function driveMuscleJointMotor(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  def: HumanoidJointDef,
  command: MuscleJointCommand,
  commandOut?: HumanoidMotorCommandBuffer,
): void {
  const joint = humanoid.joints[def.name];
  const maxTorque = Math.max(command.maxTorqueNm, 0);
  if (maxTorque <= 1e-9) {
    if (def.type === 'revolute') world.setRevoluteMotor(joint, null);
    else world.setSphericalMotor(joint, null);
    return;
  }

  const targetRelative = muscleCommandToTargetRelative(def, command);
  const error = computeJointPoseError(world, humanoid, buffer, def, targetRelative);
  const speedLimit = muscleMotorSpeedLimitRadS(command);

  if (def.type === 'revolute') {
    const targetSpeed = clampScalar(
      MUSCLE_MOTOR_ANGLE_KP_PER_S * error.twistRad,
      speedLimit,
    );
    world.setRevoluteMotor(joint, { speed: targetSpeed, maxTorque });
    commandOut?.set(def.name, {
      commandedSpeed: targetSpeed,
      torqueCap: maxTorque,
      revolute: true,
      // Diagnostic only — signed target vs tracking (no control path reads these).
      targetOffsetRad: command.targetOffsetRad,
      targetRotationVectorRad: [
        command.targetRotationVectorRad[0],
        command.targetRotationVectorRad[1],
        command.targetRotationVectorRad[2],
      ],
      poseErrorTwistRad: error.twistRad,
    });
  } else {
    const rawVelocity: Vec3Tuple = [
      error.errorAxisAngle[0] * MUSCLE_MOTOR_ANGLE_KP_PER_S,
      error.errorAxisAngle[1] * MUSCLE_MOTOR_ANGLE_KP_PER_S,
      error.errorAxisAngle[2] * MUSCLE_MOTOR_ANGLE_KP_PER_S,
    ];
    const velocity = clampVectorMagnitude(rawVelocity, speedLimit);
    world.setSphericalMotor(joint, { velocity, maxTorque });
    commandOut?.set(def.name, {
      commandedSpeed: Math.hypot(velocity[0], velocity[1], velocity[2]),
      torqueCap: maxTorque,
      revolute: false,
      // Diagnostic only — signed target vs tracking (no control path reads these).
      targetOffsetRad: command.targetOffsetRad,
      targetRotationVectorRad: [
        command.targetRotationVectorRad[0],
        command.targetRotationVectorRad[1],
        command.targetRotationVectorRad[2],
      ],
      poseErrorAxisAngleRad: [
        error.errorAxisAngle[0],
        error.errorAxisAngle[1],
        error.errorAxisAngle[2],
      ],
      // Solver/motor-coordinate commanded velocity (same post-clamp tuple sent
      // to setSphericalMotor). Report-only; not world-frame torque.
      commandedVelocityVectorRadS: [velocity[0], velocity[1], velocity[2]],
    });
  }
}

function applyMuscleJointCommandImpulse(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  def: HumanoidJointDef,
  command: MuscleJointCommand,
): void {
  const maxTorque = Math.max(command.maxTorqueNm, 0);
  if (maxTorque <= 1e-9) return;

  const parentBody = humanoid.bodies[def.parent];
  const childBody = humanoid.bodies[def.child];
  const targetRelative = muscleCommandToTargetRelative(def, command);
  const error = computeJointPoseError(world, humanoid, buffer, def, targetRelative);

  let errorAxisAngle: Vec3Tuple;
  let relVelAlongAxis: Vec3Tuple;
  if (def.type === 'revolute') {
    const hingeAxis = humanoid.jointAxes[def.name] ?? [0, 0, 1];
    const relVelOnAxis = dotVec3(error.relativeAngularVelocity, hingeAxis);
    errorAxisAngle = [hingeAxis[0] * error.twistRad, hingeAxis[1] * error.twistRad, hingeAxis[2] * error.twistRad];
    relVelAlongAxis = [hingeAxis[0] * relVelOnAxis, hingeAxis[1] * relVelOnAxis, hingeAxis[2] * relVelOnAxis];
  } else {
    errorAxisAngle = error.errorAxisAngle;
    relVelAlongAxis = error.relativeAngularVelocity;
  }

  const rawTorque: Vec3Tuple = [
    error.childInertia * (MUSCLE_IMPULSE_KP * errorAxisAngle[0] - MUSCLE_IMPULSE_KD * relVelAlongAxis[0]),
    error.childInertia * (MUSCLE_IMPULSE_KP * errorAxisAngle[1] - MUSCLE_IMPULSE_KD * relVelAlongAxis[1]),
    error.childInertia * (MUSCLE_IMPULSE_KP * errorAxisAngle[2] - MUSCLE_IMPULSE_KD * relVelAlongAxis[2]),
  ];
  const torque = clampVectorMagnitude(rawTorque, Math.max(maxTorque, 1e-6));

  // Equal-and-opposite joint torques only — never a root/pelvis external force.
  world.applyTorque(childBody, torque);
  world.applyTorque(parentBody, [-torque[0], -torque[1], -torque[2]]);
}

/**
 * Apply per-joint muscle commands to the live rig.
 *
 * - `caps.jointMotors === true` → solver-integrated motors, torque budget
 *   taken from each command's `maxTorqueNm`.
 * - otherwise (stale WASM / missing motors) → equal-and-opposite `applyTorque`
 *   on the joint's parent/child pair only.
 *
 * Only joints present in `commands` are driven; omitted joints are untouched
 * (call `disableAllJointMotors` when switching away from muscle control).
 * Does not apply stand-assist, upright balance, forces, impulses, or teleports.
 *
 * When `commandOut` is provided it is cleared then filled with this step's
 * commanded maxTorque + motor speed/velocity magnitude for every active motor
 * (torqueCap > 0). Callers query actual solver torque after `world.step`.
 */
export function applyMuscleJointCommands(
  world: World,
  humanoid: Humanoid,
  buffer: TransformBuffer,
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  caps?: HumanoidMotorCapabilities,
  commandOut?: HumanoidMotorCommandBuffer,
): void {
  const useMotors = caps?.jointMotors === true;
  commandOut?.clear();
  // Drive from the rig's actual joint types (muscleBalance spherical ankles).
  for (const def of jointDefsOf(humanoid)) {
    const command = commands[def.name];
    if (!command) continue;
    if (useMotors) {
      driveMuscleJointMotor(world, humanoid, buffer, def, command, commandOut);
    } else {
      applyMuscleJointCommandImpulse(world, humanoid, buffer, def, command);
    }
  }
}
